/**
 * Minutes the sweeper waits before re-driving, indexed
 * by the attempt that just failed. Covers roughly 2.5
 * hours of upstream outage.
 */
export const PIPELINE_REDRIVE_BACKOFF_MINUTES = [5, 30, 120] as const;

/**
 * Pipeline runs allowed per document. Counts runs, not
 * the in-run Inngest retries inside a single run.
 *
 * Derived rather than written down: the upload commit
 * dispatches run 1, and each backoff above buys exactly one
 * re-drive. A hardcoded value silently strands the tail of
 * the backoff array — at 3 the 120-minute step was
 * unreachable, so the schedule covered 35 minutes of outage
 * instead of the 2.5 hours it claims.
 */
export const MAX_PIPELINE_ATTEMPTS =
  PIPELINE_REDRIVE_BACKOFF_MINUTES.length + 1;

/**
 * How far back the sweeper looks for failures to re-drive,
 * measured from the last meta write.
 *
 * A row that exhausts its attempts keeps `failureKind`
 * `transient` or `stalled` forever — the kind records why it
 * failed, not whether anything will retry it. Without a
 * bound those rows accumulate in the candidate window and,
 * once there are enough of them, newer recoverable failures
 * never load and re-drive stops silently. Ageing them out
 * keeps the window bounded by recent activity.
 *
 * Comfortably longer than the whole backoff schedule, so a
 * document cannot age out with a re-drive still owed.
 */
export const PIPELINE_REDRIVE_WINDOW_HOURS = 12;

/**
 * A document whose `stageStartedAt` is older than this
 * has lost its run — no step can legitimately take
 * this long.
 */
export const PIPELINE_STALL_THRESHOLD_MINUTES = 20;

/**
 * How long a document may sit queued before it is presumed
 * to have lost its trigger. Far longer than the processing
 * threshold: under a large backlog a document can legitimately
 * wait its turn, and re-driving one that is merely queued
 * would double-fire it.
 */
export const PIPELINE_PENDING_GRACE_MINUTES = 60;
